import ImageLayer from 'ol/layer/Image';
import ImageStatic from 'ol/source/ImageStatic';
import { useCallback, useEffect, useRef } from 'react';
import { useElement, useLayers } from '@ais-ui/react-openlayers';

interface CoverageImageWMSLayerProps {
  overlay: string | null;
  extent: number[]; // [minX, minY, maxX, maxY]
  name: string;
  properties: Record<string, any>;
}

export function CoverageImageWMSLayer({
  overlay,
  extent,
  name,
  properties,
}: CoverageImageWMSLayerProps) {
  const { removeLayer } = useLayers();

  // Initialize the layer immediately
  const elementRef = useRef<ImageLayer<ImageStatic>>(
    new ImageLayer({
      source: new ImageStatic({
        url: `data:image/png;base64,${overlay}`,
        imageExtent: extent,
      }),
    }),
  );

  const prevOverlay = useRef<string | null>(overlay);
  const prevExtent = useRef<number[]>(extent);

  useEffect(() => {
    // Check if overlay or extent actually changed
    const overlayChanged = overlay !== prevOverlay.current;
    const extentChanged =
      JSON.stringify(extent) !== JSON.stringify(prevExtent.current);

    if (!overlayChanged && !extentChanged) {
      return;
    }
    prevOverlay.current = overlay;
    prevExtent.current = extent;

    // Remove old layer
    if (elementRef.current) {
      removeLayer(elementRef.current);
    }

    // Create and assign new layer
    const newLayer = new ImageLayer({
      source: new ImageStatic({
        url: `data:image/png;base64,${overlay}`,
        imageExtent: extent,
      }),
    });

    elementRef.current = newLayer;
  }, [overlay, extent, removeLayer, name]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (elementRef.current) {
        removeLayer(elementRef.current);
      }
    };
  }, [removeLayer, name]);

  const canZoomToFromLayerControl = true;
  const isRemovableFromLayerControl = true;

  useElement(
    elementRef.current,
    name,
    canZoomToFromLayerControl,
    isRemovableFromLayerControl,
    properties,
  );

  return null;
}
